import React from 'react';
import { createDataSourceOption, lookupTypeOtions, endpointLookupOption } from './LookupBuilder.Helpers';
import { ReactComponent as RemoveIcon } from '../../Common/BootstrapIcons/close.svg';
import { ReactComponent as ViewIcon } from '../../Common/BootstrapIcons/view.svg';
import { ReactComponent as SwapIcon } from '../../Common/BootstrapIcons/shuffle.svg';

import { reportType } from '../../../infrastructure/constants/reportType';
import BespokeToggleButton from '../../Functions/Bespoke/BespokeToggleButton';
import ColumnDefinition from '../../../infrastructure/types/DataGrid/ColumnDefinition';
import CustomIcon from '../../Common/BootstrapIcons/Custom/CustomIcon';
import dataSourceHelper from '../../../infrastructure/helpers/functions/common/dataSourceHelper';
import EditableText from '../../Common/EditableText/EditableText';
import enpointLookupAdapter from '../../../infrastructure/helpers/functions/lookup/enpointLookupAdapter';
import iconTooltip from '../../Common/BootstrapIcons/Tooltip/IconTooltip';
import IngestionConfig from '../../../types/ingestion/IngestionConfig';
import Lookup from '../../../types/functions/Lookup';
import ReportCollections from '../../../infrastructure/types/Functions/ReportCollections';
import SelectList from '../../Common/SelectList/SelectList';
import typeHelper from '../../../infrastructure/helpers/common/typeHelper';
import DraggableItem from '../../Common/Dragging/DraggableItem/DraggableItem';
import Mode from '../../../types/report/Mode';
import ComponentType from '../../../types/report/ComponentType';
import ComponentChangeLogIconAndAction from '../../Common/Modals/ComponentChangeLogModal/ComponentChangeLogIconAndAction';
import CustomDropdown from '../../Common/Dropdown/CustomDropdown';
import CustomDropdownOption from '../../Common/Dropdown/CustomDropdownOption';
import ReportConfig from '../../../types/report/ReportConfig';
import Spinner from '../../Common/Spinner/Spinner';
import Method from '../../../types/report/Method';
import SelectOption from '../../../infrastructure/types/SelectOption';


const getDataSource1Content = (lookup: Lookup, dataSources: IngestionConfig[], onDataSourceChange: (dataSourceId: string, lookupNumber: number) => void, type: string, isReadOnly: boolean, onLockSectionClick?: (number: number) => Promise<boolean>): JSX.Element | string => {

    if (lookup.dataSource1Id && ((isReadOnly || (lookup.isMultiResult && (lookup.condition.and || lookup.condition.or))) || lookup.isBespoke)) {

        return dataSourceHelper.getById(dataSources, lookup.dataSource1Id as number).name;
    }

    let options = dataSources.map(createDataSourceOption);

    if (type !== reportType.directReporting && type !== reportType.globalModule) {

        options.push(endpointLookupOption);
    }

    const onChangeCheckIfLocked = async (value: string, number: number, event: React.ChangeEvent<HTMLElement>): Promise<void> => {

        const isLocked = onLockSectionClick && await onLockSectionClick(number);
        if (!isLocked) {
            onDataSourceChange(value, number);
        }
    }

    return (
        <SelectList
            includeEmptyOption={type === reportType.globalModule}
            className='inline-select'
            disabled={isReadOnly || type === reportType.accuracy || lookup.globalLookupModuleId as number > 0}
            options={options}
            value={enpointLookupAdapter.getValue(lookup)}
            onChange={(value, event) => { onLockSectionClick ? onChangeCheckIfLocked(value, lookup.number, event) : onDataSourceChange(value, lookup.number) }} />
    );
};

const getDataSource2Content = (lookup: Lookup, dataSources: IngestionConfig[], onDataSourceChange: (dataSourceId: string, lookupNumber: number) => void, isReadOnly: boolean, onLockSectionClick?: (number: number) => Promise<boolean>,): JSX.Element | string => {

    let isRegular = !lookup.isBespoke;

    if (lookup.dataSource2Id && ((isReadOnly || (lookup.isMultiResult && (lookup.condition.and || lookup.condition.or))) || lookup.isBespoke)) {

        return dataSourceHelper.getById(dataSources, lookup.dataSource2Id as number).name;
    }

    const onChangeCheckIfLocked = async (value: string, number: number, event: React.ChangeEvent<HTMLElement>): Promise<void> => {

        const isLocked = onLockSectionClick && await onLockSectionClick(number);
        if (!isLocked) {
            onDataSourceChange(value, number);
        }
    }

    return (
        <SelectList
            includeEmptyOption={isRegular}
            className='inline-select'
            disabled={isReadOnly || lookup.globalLookupModuleId as number > 0}
            options={dataSources.map(createDataSourceOption)}
            value={typeHelper.isNumber(lookup.dataSource2Id) ? (lookup.dataSource2Id as number).toString() : ''}
            onChange={(value, event) => { onLockSectionClick ? onChangeCheckIfLocked(value, lookup.number, event) : onDataSourceChange(value, lookup.number) }} />
    );
};

const getNumberContent = (number: number, reorder: (dragNumber: number, dropNumber: number) => void, isReadOnly: boolean): string | JSX.Element => {
    if (isReadOnly) {
        return number.toString();
    }
    return (
        <DraggableItem key={number} identifier={number} onDrop={reorder}>
            <span>{number}</span>
        </DraggableItem>
    );
};

const onLookupTypeChangeInDropdown = async (value: boolean, number: number, event: React.ChangeEvent<HTMLElement>,
    onLockSectionClick: (number: number) => Promise<boolean>,
    onLookupTypeChange: (isMultiResult: boolean, number: number) => void) => {
    const isLocked = await onLockSectionClick(number);
    if (!isLocked) {
        onLookupTypeChange(value, number);
    }
    else {
        event.preventDefault();
    }
}

const getLookupMode = (lookup: Lookup, onGlobalLookupLinkClick: (lookupId: number) => void): JSX.Element => {

    return <div className='display-flex'>
        <span>{lookup.mode}</span>
        {lookup.mode === Mode.Global &&
            <button
                className='custom-icon-button'
                onClick={(event) => {
                    onGlobalLookupLinkClick(lookup.globalLookupModuleId as number)
                }}
            >
                <CustomIcon className='main-blue-color icon-small' icon={ViewIcon} title={iconTooltip.view} />
            </button>
        }
    </div>
}

const lookupMethodOtions: SelectOption[] = [
    {
        text: Method.Lookup,
        value: Method.Lookup.toString()
    },
    {
        text: Method.Indexed,
        value: Method.Indexed.toString()
    }
];

const getColumns = (
    onLockSectionClick: (number: number) => Promise<boolean>,
    onDataSource1Change: (dataSourceId: string, number: number) => void,
    onDataSource2Change: (dataSourceId: string, number: number) => void,
    onNameChange: (name: string, number: number) => void,
    onLookupTypeChange: (isMultiResult: boolean, number: number) => void,
    onBespokeToggleClick: (isBespoke: boolean, lookup: Lookup, event?: React.MouseEvent<HTMLElement>) => void,
    onRemoveClick: (number: number, setup?: (x: Lookup[]) => void, event?: React.MouseEvent<HTMLElement>) => void,
    getLookupReferences: (number: number) => Promise<CustomDropdownOption[]>,
    reorder: (dragNumber: number, dropNumber: number) => void,
    collections: ReportCollections,
    isReadOnly: boolean,
    onLookupReferenceSelected: (option: CustomDropdownOption) => void,
    onGlobalLookupLinkClick: (lookupId: number) => void,
    reportConfig: ReportConfig,
    onChangeModeClick: (lookup: Lookup) => void,
    isSwapOptionReadOnly: (lookup: Lookup) => boolean,
    onLookupMethodChange: (method: string, lookup: Lookup) => void,
    showSpinnerForId?: number,
): ColumnDefinition<Lookup>[] => {

    let columns: ColumnDefinition<Lookup>[] = [
        {
            header: 'Reference',
            getContent: x => getNumberContent(x.number, reorder, isReadOnly),
            width: '7%'
        },
        {
            header: 'Lookup Name',
            getContent: l => <EditableText key={l.name}
                disabled={l.isReadOnly || isReadOnly}
                value={l.name}
                onChange={value => onNameChange(value, l.number)}
                multiline={true} />,
            width: '15%'
        },
        {
            header: 'Mode',
            getContent: l => getLookupMode(l, onGlobalLookupLinkClick),
            width: '8%'
        },
        {
            header: 'Lookup Type',
            getContent: l =>
                <SelectList
                    className='inline-select'
                    disabled={l.isReadOnly || isReadOnly}
                    options={lookupTypeOtions}
                    value={l.isMultiResult.toString()}
                    onChange={((value, event) => { onLookupTypeChangeInDropdown(value === true.toString(), l.number, event, onLockSectionClick, onLookupTypeChange) })} />,
            width: '10%'
        },
        // {
        //     header: 'Method',
        //     getContent: l => <SelectList
        //         className='inline-select'
        //         disabled={true}
        //         options={lookupMethodOtions}
        //         value={l.method}
        //         onChange={((value, event) => { onLookupMethodChange(value, l) })} />,

        //     width: '8%'
        // },
        {
            header: (l) => {
                return !l.method || l.method === Method.Lookup.toString() ? 'From' : 'To'
            },
            getContent: l =>!l.method || l.method === Method.Lookup.toString() ?
                getDataSource1Content(l, collections.dataSources, onDataSource1Change, reportConfig.type, l.isReadOnly || isReadOnly, onLockSectionClick) :
                getDataSource2Content(l, collections.dataSources, onDataSource2Change, l.isReadOnly || isReadOnly, onLockSectionClick),
            width: '15%',
            className: 'break-all'
        },
        {
            header: '',
            getContent: l => <></>,
            width: '2%'
        }];

    columns.push({
        header: (l) => {
            return !l.method || l.method === Method.Lookup.toString() ? 'To' : ''
        },
        getContent: l => !l.method || l.method === Method.Lookup.toString() ?
            getDataSource2Content(l, collections.dataSources, onDataSource2Change, l.isReadOnly || isReadOnly, onLockSectionClick) : <></>,
        className: 'break-all',
        width: '15%',
    });

    columns.push({
        header: '',
        getContent: l =>
            <div className='display-flex justify-content-between'>
                <CustomDropdown
                    optionsPopupClassName='max-width-350px'
                    showOnlyDefaultTextAsSelected='References'
                    getOptions={async () => { return getLookupReferences(l.number) }}
                    value={l.name}
                    onOptionSelected={(option: CustomDropdownOption) => {
                        onLookupReferenceSelected(option);
                    }}
                    optionsAsHiperlinks={reportConfig.type === reportType.globalModule}
                />
                {
                    !isReadOnly && !l.isReadOnly &&
                    collections.dataSources.some(x => x.id !== l.dataSource1Id) &&
                    <BespokeToggleButton isBespoke={l.isBespoke} onClick={(isBespoke, event) => onBespokeToggleClick(isBespoke, l, event)} />
                }
                <div className='display-flex align-items-center margin-left-10px'>
                    {l.id === showSpinnerForId ?
                        <Spinner className='margin-right-10px' />
                        : <button
                            className='custom-icon-button'
                            onClick={(event) => onRemoveClick(l.number, undefined, event)}
                            disabled={isReadOnly}>
                            <CustomIcon icon={RemoveIcon} title={iconTooltip.remove} />
                        </button>
                    }
                    {l.id &&
                        <ComponentChangeLogIconAndAction
                            reportConfigId={reportConfig.id as number}
                            id={l.id as number}
                            componentName={l.name}
                            componentType={ComponentType.Lookup} />
                    }
                    {reportConfig.type !== reportType.accuracy &&
                        <button disabled={isSwapOptionReadOnly(l)}
                            type="button" className="custom-icon-button button-spinner-font-size" onClick={() => onChangeModeClick(l)}>
                            <CustomIcon icon={SwapIcon} title={iconTooltip.swapLookup} />
                        </button>
                    }
                </div>
            </div >,
    }
    );

    return columns;
};

export {
    getColumns
};